import React from "react";
import { useCurrency } from "../../../hooks/useCurrency";
import DetailsModal from "../../../components/forms/DetailsModal";
import detailStyles from "../../../components/forms/DetailsModal.module.css";

export default function ResourceDetailModal({
  resource,
  canEditResources,
  canDeleteResources,
  getResourceTypeLabel,
  getAvailabilityLabel,
  getAvailabilityColor,
  onClose,
  onEditClick,
  onArchiveClick,
  onRestoreClick,
}) {
  const { format: formatMoney } = useCurrency();

  return (
    <DetailsModal
      title="Resource Details"
      onClose={onClose}
      onEdit={canEditResources ? onEditClick : null}
      onDelete={resource.is_active ? (canDeleteResources ? onArchiveClick : null) : null}
      deleteLabel="Archive Resource"
      editLabel="Edit Resource"
    >
      {/* Resource Header */}
      <div className={detailStyles.detailSection}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '20px',
          background: resource.type === 'vehicle'
            ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
            : resource.type === 'tool'
              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
              : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
          borderRadius: '12px',
          marginBottom: '8px'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            flexShrink: 0
          }}>
            {resource.type === 'vehicle' ? '🚗' : resource.type === 'tool' ? '🔧' : '📦'}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: '600', color: 'white' }}>
              {resource.name}
            </h3>
            <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
              {getResourceTypeLabel(resource.type)}
            </p>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            backgroundColor: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px'
          }}>
            <span style={{
              width: '8px', height: '8px', borderRadius: '50%',
              backgroundColor: resource.is_active ? getAvailabilityColor(resource.availability_status) : '#ef4444'
            }} />
            <span style={{ color: 'white', fontSize: '0.85rem' }}>
              {resource.is_active ? getAvailabilityLabel(resource.availability_status) : 'Archived'}
            </span>
          </div>
        </div>
      </div>

      {!resource.is_active && canDeleteResources && (
        <div style={{ margin: '0 0 12px', textAlign: 'center' }}>
          <button
            onClick={onRestoreClick}
            style={{
              padding: '8px 20px',
              backgroundColor: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '500'
            }}
          >
            Restore Resource
          </button>
        </div>
      )}

      {/* General Details */}
      <div className={detailStyles.detailSection}>
        <h4 className={detailStyles.sectionTitle}>Details</h4>
        {resource.description && (
          <div className={detailStyles.detailRow}>
            <span className={detailStyles.detailLabel}>Description</span>
            <span className={detailStyles.detailValue}>{resource.description}</span>
          </div>
        )}
      </div>

      {/* Vehicle Details */}
      {resource.type === 'vehicle' && (
        <div className={detailStyles.detailSection}>
          <h4 className={detailStyles.sectionTitle}>Vehicle Details</h4>
          <div className={detailStyles.detailRow}>
            <span className={detailStyles.detailLabel}>Registration</span>
            <span className={`${detailStyles.detailValue} ${!resource.registration_number ? detailStyles.empty : ''}`}>
              {resource.registration_number || '—'}
            </span>
          </div>
          <div className={detailStyles.detailRow}>
            <span className={detailStyles.detailLabel}>Vehicle Type</span>
            <span className={`${detailStyles.detailValue} ${!resource.vehicle_type ? detailStyles.empty : ''}`}>
              {resource.vehicle_type || '—'}
            </span>
          </div>
          <div className={detailStyles.detailRow}>
            <span className={detailStyles.detailLabel}>Capacity</span>
            <span className={`${detailStyles.detailValue} ${!resource.capacity ? detailStyles.empty : ''}`}>
              {resource.capacity || '—'}
            </span>
          </div>
          <div className={detailStyles.detailRow}>
            <span className={detailStyles.detailLabel}>Insurance Expiry</span>
            <span className={`${detailStyles.detailValue} ${!resource.insurance_expiry ? detailStyles.empty : ''}`}>
              {resource.insurance_expiry ? new Date(resource.insurance_expiry).toLocaleDateString() : '—'}
            </span>
          </div>
          <div className={detailStyles.detailRow}>
            <span className={detailStyles.detailLabel}>Inspection Expiry</span>
            <span className={`${detailStyles.detailValue} ${!resource.inspection_expiry ? detailStyles.empty : ''}`}>
              {resource.inspection_expiry ? new Date(resource.inspection_expiry).toLocaleDateString() : '—'}
            </span>
          </div>
        </div>
      )}

      {/* Accounting */}
      <div className={detailStyles.detailSection}>
        <h4 className={detailStyles.sectionTitle}>Accounting</h4>
        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Asset Value</span>
          <span className={`${detailStyles.detailValue} ${!resource.asset_value ? detailStyles.empty : ''}`}>
            {resource.asset_value ? (
              <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontWeight: '500', fontSize: '0.9rem' }}>
                {formatMoney(resource.asset_value)}
              </span>
            ) : '—'}
          </span>
        </div>
        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Running Cost</span>
          <span className={`${detailStyles.detailValue} ${!resource.running_cost ? detailStyles.empty : ''}`}>
            {resource.running_cost ? formatMoney(resource.running_cost) : '—'}
          </span>
        </div>
        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Depreciation Rate</span>
          <span className={`${detailStyles.detailValue} ${!resource.depreciation_rate ? detailStyles.empty : ''}`}>
            {resource.depreciation_rate ? `${resource.depreciation_rate}%` : '—'}
          </span>
        </div>
        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Cost Center</span>
          <span className={`${detailStyles.detailValue} ${!resource.cost_center ? detailStyles.empty : ''}`}>
            {resource.cost_center || '—'}
          </span>
        </div>
      </div>
    </DetailsModal>
  );
}
